import { z } from '@/i18n';
import { useAuthStore } from '@/store/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormError, FormField, FormLabel, FormLabelGroup, Input, Link } from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ErrorResponse } from 'oidc-client-ts';
import { useEffect } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SigningInCover } from './SigningInCover';

type EmailAuthProps = {
  onAuthSuccess: (email: string, password: string, isDirectLogin: boolean) => void;
  onAuthError: (error: ErrorResponse) => void;
  redirectUrl?: string;
};

interface LoginInput {
  email: string;
  password: string;
}

const emailFormSchema = z.object({
  email: z.string().email().min(1).max(100),
  password: z.string().min(1).max(100),
});

export const EmailAuth = ({ onAuthSuccess, onAuthError, redirectUrl }: EmailAuthProps) => {
  const { t } = useTranslation();
  const { authService, setAuthService } = useAuthStore((state) => state);
  const navigate = useNavigate();

  const {
    formState: { errors },
    setValue,
    handleSubmit,
    control,
  } = useForm<LoginInput>({
    values: {
      email: '',
      password: '',
    },
    resolver: zodResolver(emailFormSchema),
  });

  const { field: emailField } = useController({
    name: 'email',
    control,
  });

  const { field: passwordField } = useController({
    name: 'password',
    control,
  });

  const { mutate: handleLogin, isPending: isLoginLoading } = useMutation({
    mutationFn: async (data: { username: string; password: string }) => {
      console.debug('[/login]: Logging in...');

      try {
        const oidcUser = await authService!.signinResourceOwnerCredentials(data.username, data.password);
        onAuthSuccess(data.username, data.password, !!oidcUser);
      } catch (err) {
        const errorResponse = err as ErrorResponse;
        console.log('errorResponse', errorResponse);

        // Check if the error is "Account is not fully set up"
        if (
          errorResponse.error === 'invalid_grant' &&
          errorResponse.error_description === 'Account is not fully set up'
        ) {
          // Navigate to verify-email page with the email
          navigate({
            to: '/verify-email',
            search: {
              email: data.username,
              redirect: redirectUrl,
            },
          });
          return;
        }

        onAuthError(errorResponse);
      }
    },
  });

  const onSubmit: SubmitHandler<LoginInput> = async ({ email: username, password }) => {
    await handleLogin({ username, password });
  };

  useEffect(() => {
    setAuthService('email');
  }, [setAuthService]);

  if (isLoginLoading) {
    return <SigningInCover title={t('login.loadingTitle')} subtitle={t('login.loadingSubtitle')} />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-10">
        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.email}>{t('login.authMethods.email.fields.email.label')}</FormLabel>
            {errors.email && <FormError>{t(errors.email.message as unknown as TemplateStringsArray)}</FormError>}
          </FormLabelGroup>

          <Input
            disabled={isLoginLoading}
            error={!!errors.email}
            value={emailField.value}
            onChange={emailField.onChange}
            onClear={() => {
              setValue('email', '');
            }}
          ></Input>
        </FormField>

        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.password}>{t('login.authMethods.email.fields.password.label')}</FormLabel>
            {errors.password && <FormError>{t(errors.password?.message as unknown as TemplateStringsArray)}</FormError>}
          </FormLabelGroup>

          <Input
            type="password"
            disabled={isLoginLoading}
            error={!!errors.password}
            value={passwordField.value}
            onChange={passwordField.onChange}
            autoComplete="current-password"
            onClear={() => {
              setValue('password', '');
            }}
          ></Input>
          <Link
            className="self-start"
            type="button"
            onClick={() =>
              navigate({
                to: '/forgot-password',
                search: { redirect: redirectUrl },
              })
            }
          >
            {t('login.authMethods.email.fields.password.hint')}
          </Link>
        </FormField>
      </div>

      <Button className="w-full my-10" type="submit" disabled={isLoginLoading}>
        {t('login.authMethods.email.actionButtonLabel')}
      </Button>

      <span className="block text-muted-foreground mb-8">
        {t('login.authMethods.email.noAccount')}{' '}
        <Link
          type="button"
          onClick={() => {
            navigate({
              to: '/signup',
              search: {
                txId: undefined,
                mpkId: false,
                redirect: redirectUrl,
              },
            });
          }}
        >
          {t('login.authMethods.email.createAccount')}
        </Link>
      </span>
    </form>
  );
};
